'use client';

import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ForwardedRef,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { SList, type SListProps } from '../SList';
import { useSDraggableGroup } from './SDraggableGroup';

export interface SDraggableListRenderState {
  onDragHandleMouseDown: (event: MouseEvent<HTMLDivElement>) => void;
  selected: boolean;
}

export interface SDraggableListProps<T> extends Omit<SListProps, 'children' | 'onChange'> {
  /** 현재 순서대로 렌더링할 아이템 목록 */
  items: T[];
  /** 아이템을 식별할 안정적인 key */
  getKey: (item: T, index: number) => string;
  /** 아이템 렌더링 함수 */
  renderItem: (item: T, index: number, state: SDraggableListRenderState) => ReactNode;
  /** 단독 리스트에서 드래그가 끝난 뒤 변경된 순서 */
  onChange?: (items: T[]) => void;
  /** 외부에서 제어하는 selected 아이템 key */
  selectedKey?: string;
  /** 초기 selected 아이템 key */
  defaultSelectedKey?: string;
  /** selected 아이템이 변경될 때 호출 */
  onSelectedKeyChange?: (key: string) => void;
  /** disabled 아이템은 선택 및 드래그에서 제외 */
  getDisabled?: (item: T, index: number) => boolean;
  /** Provider 안에서 사용할 리스트 식별자 */
  listId?: string;
  /** 같은 group 값을 가진 리스트끼리 드래그 이벤트를 공유 */
  group?: string;
}

function reorder<T>(items: T[], fromIndex: number, toIndex: number) {
  if (fromIndex === toIndex) return items;
  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);
  return nextItems;
}

function SDraggableListInner<T>(
  {
    items,
    getKey,
    renderItem,
    onChange,
    selectedKey,
    defaultSelectedKey,
    onSelectedKeyChange,
    getDisabled,
    listId,
    group: groupName,
    useGap,
    className,
    ...rest
  }: SDraggableListProps<T>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const dragContext = useSDraggableGroup();
  const dragGroup = groupName ?? dragContext?.defaultGroup;
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [internalSelectedKey, setInternalSelectedKey] = useState<string | undefined>(
    defaultSelectedKey,
  );
  const listRef = useRef<HTMLDivElement>(null);
  const fromIndexRef = useRef<number | null>(null);
  const currentSelectedKey = selectedKey ?? internalSelectedKey;

  const startDrag = useCallback((event: MouseEvent<HTMLDivElement>, index: number) => {
    event.preventDefault();
    fromIndexRef.current = index;
    setDraggingIndex(index);
    setDropIndex(index);
  }, []);

  useEffect(() => {
    if (dragContext || draggingIndex == null) return undefined;

    const handleMouseMove = (event: globalThis.MouseEvent) => {
      const itemElements = listRef.current?.querySelectorAll<HTMLElement>(
        '[data-draggable-list-item]',
      );
      if (!itemElements) return;

      let nextDropIndex = items.length;
      itemElements.forEach((itemElement, index) => {
        const rect = itemElement.getBoundingClientRect();
        if (event.clientY < rect.top + rect.height / 2 && nextDropIndex === items.length) {
          nextDropIndex = index;
        }
      });
      setDropIndex(nextDropIndex);
    };

    const handleMouseUp = () => {
      const fromIndex = fromIndexRef.current;
      const nextDropIndex = dropIndex;
      if (fromIndex != null && nextDropIndex != null) {
        const toIndex = nextDropIndex > fromIndex ? nextDropIndex - 1 : nextDropIndex;
        if (fromIndex !== toIndex) onChange?.(reorder(items, fromIndex, toIndex));
      }
      fromIndexRef.current = null;
      setDraggingIndex(null);
      setDropIndex(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingIndex, dropIndex, items, onChange, dragContext]);

  if (dragContext && (!listId || !dragGroup)) {
    throw new Error('SDraggableList: Provider 안에서는 group과 listId가 필요합니다.');
  }

  const groupDrop = dragContext?.drop;
  const currentDropIndex = dragContext
    ? groupDrop?.group === dragGroup && groupDrop?.listId === listId
      ? (groupDrop?.index ?? null)
      : null
    : dropIndex;
  const isDropTarget = dragContext
    ? groupDrop?.group === dragGroup && groupDrop?.listId === listId
    : draggingIndex != null;
  const showEmptyDrop = Boolean(dragContext && isDropTarget && items.length === 0);
  const beforeIndicatorPosition = useGap ? 'before:top-[-5px]' : 'before:top-0';
  const afterIndicatorPosition = useGap ? 'after:bottom-[-5px]' : 'after:bottom-[-1px]';

  const setListRef = (node: HTMLDivElement | null) => {
    listRef.current = node;
    if (dragContext && dragGroup && listId) {
      dragContext.registerList(
        dragGroup,
        listId,
        node,
        items,
        onChange ? nextItems => onChange(nextItems as T[]) : undefined,
      );
    }
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  return (
    <SList
      ref={setListRef}
      {...rest}
      useGap={useGap}
      className={`${className ?? ''} ${dragContext ? 'min-h-[40px]' : ''} ${
        showEmptyDrop
          ? "relative before:absolute before:left-0 before:right-0 before:top-0 before:z-10 before:h-[2px] before:bg-(--sys-color-border-accent) before:content-['']"
          : ''
      }`}
      data-draggable-group={dragContext ? dragGroup : undefined}
      data-draggable-list-id={dragContext ? listId : undefined}
    >
      {items.map((item, index) => {
        const itemKey = getKey(item, index);
        const disabled = getDisabled?.(item, index) ?? false;
        const showBefore = isDropTarget && currentDropIndex === index;
        const showAfter =
          isDropTarget && currentDropIndex === items.length && index === items.length - 1;
        return (
          <div
            key={itemKey}
            data-draggable-list-item
            aria-disabled={disabled || undefined}
            onClick={() => {
              if (disabled) return;
              setInternalSelectedKey(itemKey);
              onSelectedKeyChange?.(itemKey);
            }}
            className={`relative ${
              showBefore
                ? `before:absolute before:left-0 before:right-0 ${beforeIndicatorPosition} before:z-10 before:h-[2px] before:bg-(--sys-color-border-accent) before:content-['']`
                : ''
            } ${
              showAfter
                ? `after:absolute after:left-0 after:right-0 ${afterIndicatorPosition} after:z-10 after:h-[2px] after:bg-(--sys-color-border-accent) after:content-['']`
                : ''
            }`}
          >
            {renderItem(item, index, {
              onDragHandleMouseDown: event => {
                if (disabled) return;
                if (dragContext && dragGroup && listId) {
                  dragContext.startDrag(event, dragGroup, listId, index, itemKey);
                } else startDrag(event, index);
              },
              selected: currentSelectedKey === itemKey,
            })}
          </div>
        );
      })}
    </SList>
  );
}

export const SDraggableList = forwardRef(SDraggableListInner) as <T>(
  props: SDraggableListProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => ReactElement | null;
